import React, { useCallback, useEffect, useState } from "react";
import { Button, Collapse, Empty, Spin } from "antd";
import { DoubleLeftOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { NotificationManager } from "react-notifications";
import KycDesignTab from "./KycDesignTab";
import {
	DesignSectionFieldGrid,
	fetchDesignDetailsSections,
	fetchDesignMasterList,
} from "./kycDesignShared";

const { Panel } = Collapse;

const KycMaterialDesignTab = ({ productName, search = "", isActive, navigationContext = {} }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [isDetailsLoading, setIsDetailsLoading] = useState(false);
	const [viewMode, setViewMode] = useState("list");
	const [selectedDesign, setSelectedDesign] = useState(null);
	const [sectionList, setSectionList] = useState([]);
	const [activePanels, setActivePanels] = useState([]);
	const [designTable, setDesignTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [designSorter, setDesignSorter] = useState({
		field: "designMasterDate",
		order: "DESC",
	});

	const loadDesignList = useCallback(async () => {
		if (!productName) {
			setDesignTable((prev) => ({ ...prev, rows: [], totalRecords: 0 }));
			return;
		}

		setIsLoading(true);
		try {
			const result = await fetchDesignMasterList({
				materialName: productName,
				search,
				pageindex: designTable.currentPage,
				pagesize: designTable.pageSize,
				sortcolumn: designSorter.field,
				orderBy: designSorter.order,
				groupCompanyId: navigationContext.groupCompanyId || "",
			});

			setDesignTable((prev) => ({
				...prev,
				rows: result.rows,
				totalRecords: result.totalRecords,
				currentPage: result.currentPage,
			}));
		} catch (error) {
			console.log(error, "Error");
			NotificationManager.error("Unable to load design listing.");
			setDesignTable((prev) => ({ ...prev, rows: [], totalRecords: 0 }));
		} finally {
			setIsLoading(false);
		}
	}, [
		productName,
		search,
		designTable.currentPage,
		designTable.pageSize,
		designSorter.field,
		designSorter.order,
		navigationContext.groupCompanyId,
	]);

	useEffect(() => {
		if (!isActive || !productName || viewMode !== "list") {
			return undefined;
		}
		loadDesignList();
		return undefined;
	}, [isActive, productName, viewMode, loadDesignList]);

	useEffect(() => {
		if (!isActive) {
			return undefined;
		}
		setDesignTable((prev) => ({ ...prev, currentPage: 1 }));
		setViewMode("list");
		setSelectedDesign(null);
		return undefined;
	}, [search, isActive]);

	useEffect(() => {
		if (!isActive) {
			setViewMode("list");
			setSelectedDesign(null);
		}
	}, [isActive]);

	useEffect(() => {
		if (viewMode !== "details" || !selectedDesign?.generalDetailId) {
			return undefined;
		}

		let isCancelled = false;
		setIsDetailsLoading(true);
		setSectionList([]);
		setActivePanels([]);

		fetchDesignDetailsSections(selectedDesign.generalDetailId)
			.then((sections) => {
				if (isCancelled) {
					return;
				}
				setSectionList(sections);
				setActivePanels(sections.map((section) => section.groupKey));
			})
			.catch((error) => {
				console.log(error, "Error");
				if (!isCancelled) {
					NotificationManager.error("Unable to load design details.");
					setSectionList([]);
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setIsDetailsLoading(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [viewMode, selectedDesign?.generalDetailId]);

	const handleDesignTableChange = ({ currentPage, sortColumn, orderBy, pageSize }) => {
		setDesignTable((prev) => ({
			...prev,
			currentPage,
			pageSize: pageSize || prev.pageSize,
		}));
		setDesignSorter((prev) => ({
			field: sortColumn ?? prev.field,
			order: orderBy ?? prev.order,
		}));
	};

	const handleDesignSelect = (record) => {
		setSelectedDesign(record);
		setViewMode("details");
	};

	const handleBackToList = () => {
		setViewMode("list");
		setSelectedDesign(null);
		setSectionList([]);
		setActivePanels([]);
	};

	const handleCollapseChange = (keys) => {
		setActivePanels(Array.isArray(keys) ? keys : keys ? [keys] : []);
	};

	const emptyText = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />;

	const selectedDesignLabel =
		selectedDesign?.designMasterNumber && selectedDesign.designMasterNumber !== "-"
			? selectedDesign.designMasterNumber
			: "";

	if (viewMode === "details") {
		return (
			<Spin spinning={isDetailsLoading}>
				<div className="d-flex justify-content-between align-items-center details_view_title kyc-material-design-detail-topbar mb-2">
					<h4 className="kyc-design-details-page-title kyc-material-design-detail-title mb-0">
						View Design Details{selectedDesignLabel ? ` (${selectedDesignLabel})` : ""}
					</h4>
					<Button
						type="primary"
						className="back_btn"
						icon={<DoubleLeftOutlined />}
						onClick={handleBackToList}>
						Back
					</Button>
				</div>
				{sectionList.length === 0 && !isDetailsLoading ? (
					<div className="kyc-tab-placeholder">No design details found.</div>
				) : (
					<Collapse
						activeKey={activePanels}
						onChange={handleCollapseChange}
						bordered={false}
						className="kyc-mc-collapse">
						{sectionList.map((section) => {
							const isPanelOpen = activePanels.includes(section.groupKey);
							return (
								<Panel
									showArrow={false}
									key={section.groupKey}
									header={
										<span className="kyc-mc-panel-header">
											<span className="kyc-mc-panel-title">{section.sectionName}</span>
											<span className={`kyc-mc-expand-icon ${isPanelOpen ? "active" : ""}`}>
												{isPanelOpen ? <MinusOutlined /> : <PlusOutlined />}
											</span>
										</span>
									}>
									<DesignSectionFieldGrid sectionData={section.sectionData} />
								</Panel>
							);
						})}
					</Collapse>
				)}
			</Spin>
		);
	}

	return (
		<KycDesignTab
			tabTableData={designTable}
			isLoading={isLoading}
			tableSorter={designSorter}
			OnhandleTableSortColumn={handleDesignTableChange}
			navigationContext={navigationContext}
			onDesignDetailsClick={handleDesignSelect}
			emptyText={emptyText}
			tableSearchTerm={(search || "").trim()}
		/>
	);
};

export default KycMaterialDesignTab;
